import type { ExchangeScope, PublicExchange } from './types';

/**
 * One exchange as the API-key forms consume it: the key-field shape (masked
 * secrets, passphrase rules) plus the exchange's own metadata. Admin and
 * trading forms share it, so it lives here rather than in either app store.
 */
export interface ExchangeItem {
  id: string | number | symbol;
  name: string;
  comment: string | null;
  video: string | null;
  ips: string | null;
  pub?: { length: number; mask: string };
  secret?: { length: number; mask: string };
  passphrase?: { label?: string; numbersOnly?: boolean; optional?: boolean };
  /** Minimum order size in USD from the Exchanges API, or `null` when unset. */
  minOrderSize: number | null;
  crypted?: boolean;
}

export type ExchangeObjItems = Record<string, ExchangeItem>;

export const getExchangeOptions = (exchangeList?: ExchangeObjItems) =>
  Object.entries(exchangeList ?? {})
    .filter(([, exchange]) => exchange.name)
    .map(([key, { name }]) => ({ value: +key, label: name }))
    .sort((a, b) => a.label.localeCompare(b.label));

/**
 * Adapt an exchange from the caller-scoped `listPublicExchanges` endpoint (flat
 * fields) to the nested {@link ExchangeItem} shape the API-key forms consume.
 *
 * The `ips` field is scope-specific: the MM (Admin) forms use the exchange's MM
 * IPs, the ES forms use its ES IPs. Both are resolved server-side from the
 * exchange's own addresses and those of the regions it links (SKY-4606), and on
 * the public routes are narrowed to the client-visible ones.
 *
 * They are optional in the schema — the API resolves them after response
 * validation — so an absent one is normalised to `null`, which is what
 * {@link ExchangeItem} means by "no IPs".
 */
export const mapPublicExchangeToItem = (
  exchange: PublicExchange,
  scope: ExchangeScope,
): ExchangeItem => ({
  id: exchange.id,
  name: exchange.name,
  comment: exchange.comment,
  video: exchange.video,
  ips: (scope === 'es' ? exchange.ipsES : exchange.ipsMM) ?? null,
  pub: { length: exchange.pubLength, mask: exchange.pubMask },
  secret: { length: exchange.secretLength, mask: exchange.secretMask },
  passphrase: exchange.passphrase
    ? {
        label: exchange.passphraseLabel ?? undefined,
        numbersOnly: exchange.passphraseNumbersOnly,
        optional: exchange.passphraseOptional,
      }
    : undefined,
  // `0` and a missing/null value both mean "no minimum set" — normalize to
  // `null` so the task forms fall back to their default. Mirrors the admin
  // normalization in `ExchangeTab` and guards the untyped public payload
  // (which may omit the field) against leaking `0`/`undefined` downstream.
  minOrderSize:
    exchange.minOrderSize != null && exchange.minOrderSize > 0 ? exchange.minOrderSize : null,
});

/**
 * Map a caller-scoped exchange list to the keyed {@link ExchangeObjItems} shape
 * (keyed by numeric exchange id) used across the API-key selectors and forms.
 */
export const mapPublicExchangesToObj = (
  exchanges: PublicExchange[] | undefined,
  scope: ExchangeScope,
): ExchangeObjItems =>
  Object.fromEntries(
    (exchanges ?? []).map((exchange) => [exchange.id, mapPublicExchangeToItem(exchange, scope)]),
  );
